import { useState, useCallback, useEffect } from 'react';

import authRequest from 'src/utils/fetch';

type AuthRequestOptions<T> = {
  url?: string;
  optimisticResponse?: T | null;
};

export const useAuthRequestLazy = <T,>({ url }: AuthRequestOptions<T>) => {
  const [{ data, isLoading, hasError }, setState] = useState<{
    data: T | null;
    isLoading: boolean;
    hasError: boolean;
  }>({
    data: null,
    isLoading: true,
    hasError: false,
  });

  const clearData = useCallback(() => {
    setState({
      isLoading: false,
      hasError: false,
      data: null,
    });
  }, [setState]);

  const fetchData = useCallback(async () => {
    if (!url) {
      return;
    }
    setState({
      isLoading: true,
      hasError: false,
      data: null,
    });

    const response = await fetch(authRequest(url));

    if (!response.ok) {
      setState({
        data: null,
        isLoading: false,
        hasError: true,
      });
      return;
    }

    try {
      setState({
        isLoading: false,
        hasError: false,
        data: await response.json(),
      });
    } catch {
      setState({
        data: null,
        hasError: true,
        isLoading: false,
      });
    }
  }, [url]);

  return { data, isLoading, hasError, fetchData, clearData };
};

export const useAuthRequest = <T,>({ url }: AuthRequestOptions<T>) => {
  const { data, isLoading, hasError, fetchData, clearData } =
    useAuthRequestLazy<T>({ url });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    clearData();
    fetchData();
  }, [clearData, fetchData]);

  return { data, isLoading, hasError, refetch, clearData };
};
